import { AttributeDropdown, getNotChosenOption } from '@Component/AttributeDropdown/AttributeDropdown';
import { BodyDTO, EmployeeDTO } from '@Models/model.types';
import { APIError, ListResponse, showActionLogMessage } from '@tlx/astro-shared';
import {
    Alert,
    AlertContent,
    Button,
    CheckboxGroup,
    DateField,
    Form,
    Input,
    Label,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalLayout,
    ModalTitle,
} from '@tlx/atlas';
import { SpinnerButton } from '@tlx/tsk-shared';
import { callApi, computeUrl, mapObjectToUrlSearchParams, MethodType } from '@Util/api';
import { mapValidationMessages } from '@Util/apiErrorUtils';
import { dateUtil } from '@Util/date';
import { SanitizedHTMLAndMarkdown } from '@Util/SanitizedHTMLAndMarkdown';
import { APIUrls, DropdownUrls } from '@Util/urls';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SWRInfiniteKeyedMutator } from 'swr/infinite';

export function EndEmploymentsModal({
    open,
    onClose,
    employeeIds,
    mutate,
    setValidationsMap,
}: {
    open: boolean;
    onClose: () => void;
    employeeIds: number[];
    mutate: SWRInfiniteKeyedMutator<ListResponse<EmployeeDTO>[]>;
    setValidationsMap: Dispatch<SetStateAction<Map<number, string[]>>>;
}) {
    const intl = useIntl();

    const formId = 'endEmploymentForm';
    const [awaitingResponse, setAwaitingResponse] = useState(false);
    const unexpectedErrorMessage = intl.formatMessage({
        id: 'validation_unexpected_error',
    });

    const [errorsToShowInModal, setErrorsToShowInModal] = useState<string[]>([]);

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const formDate = new FormData(event.currentTarget);
        const data = Object.fromEntries(formDate.entries());
        setValidationsMap(new Map());
        const url = computeUrl(
            APIUrls.END_EMPLOYMENTS,
            mapObjectToUrlSearchParams({
                endDate: data.endDate as string,
                endReasonAmeldingTypeId: data.endReasonAmeldingTypeId as string,
                removeUserAccess: (data.removeAccessAfterEndDate === 'on').toString(),
            }),
        );
        const bodyDTO: BodyDTO = {
            employeeIds: employeeIds.join(','),
        };
        setAwaitingResponse(true);
        const { response, error } = await callApi(url, MethodType.POST, JSON.stringify(bodyDTO));
        setAwaitingResponse(false);
        if (response) {
            mutate();
            showActionLogMessage({
                pageName: intl.formatMessage({
                    id: 'heading_employee_overview',
                }),
                date: new Date(),
                message:
                    intl.formatMessage({
                        id: 'text_action_completed',
                    }) +
                    ' ' +
                    intl.formatMessage({ id: 'tsk_end_employments' }),
            });
            onClose();
        }
        if (error) {
            if (error instanceof APIError) {
                mapValidationMessages(
                    error.details.validationMessages,
                    setValidationsMap,
                    setErrorsToShowInModal,
                    unexpectedErrorMessage,
                    onClose,
                );
            }
        }
    };

    return (
        <Modal
            open={open}
            onClose={() => {
                if (!awaitingResponse) {
                    onClose();
                }
            }}
        >
            <ModalLayout>
                <ModalCloseButton />
                <ModalTitle>
                    <FormattedMessage id="tsk_end_employments" />
                </ModalTitle>
                <ModalContent>
                    {errorsToShowInModal.length > 0 && (
                        <Alert variant="error" className={'atl-mb-24'}>
                            <AlertContent>
                                <SanitizedHTMLAndMarkdown content={errorsToShowInModal.join('\n')} />
                            </AlertContent>
                        </Alert>
                    )}
                    <Form id={formId} onSubmit={onSubmit} className="atl-flex atl-flex-col atl-gap-16">
                        <DateField
                            className="atl-w-full"
                            label={intl.formatMessage({
                                id: 'text_end_date',
                            })}
                            data-testid="endDate"
                            name="endDate"
                            isRequired
                            defaultValue={dateUtil.formatDate(dateUtil.today(), 'en')}
                            validate={(value) => dateUtil.validateDateString(value, intl)}
                        />
                        <AttributeDropdown
                            text={'text_employment_end_date_reason'}
                            id={'endReasonAmeldingTypeId'}
                            url={DropdownUrls.EMPLOYMENT_END_REASONS}
                            options={getNotChosenOption(intl, '0')}
                        />
                        <CheckboxGroup>
                            <Input
                                type="checkbox"
                                data-testid="removeAccessAfterEndDate"
                                id="removeAccessAfterEndDate"
                                name="removeAccessAfterEndDate"
                            />
                            <Label htmlFor="removeAccessAfterEndDate">
                                <FormattedMessage id="text_remove_employee_access" />
                            </Label>
                        </CheckboxGroup>
                    </Form>
                </ModalContent>
                <ModalFooter className="atl-flex atl-gap-8">
                    <SpinnerButton
                        className="atl-button--primary"
                        data-testid="end-employment-run-button"
                        type="submit"
                        form={formId}
                        disabled={awaitingResponse}
                        isLoading={awaitingResponse}
                    >
                        <FormattedMessage id="button_save" />
                    </SpinnerButton>

                    <Button data-testid="cancel" variant="secondary" onClick={onClose} disabled={awaitingResponse}>
                        <FormattedMessage id="button_cancel" />
                    </Button>
                </ModalFooter>
            </ModalLayout>
        </Modal>
    );
}
